/**
 * The linked-sheet card's client preview: the page render (preview_url) zoomed ZOOM times around the link's point and
 * clamped so the box never shows past the page edge. Everything is a fraction of the box, so the box can be any width.
 *
 * The page layer is `ZOOM * 100%` of the box wide (its height follows the page's aspect); `left` / `top` are its offsets
 * as fractions of the box width / height (zero or negative), and `pinLeft` / `pinTop` put the point in the box.
 */
export const SHEET_PREVIEW_ZOOM = 3;

export interface SheetPreviewCrop {
  /** Page layer width, as a fraction of the box width. */
  width: number;
  /** Page layer offsets, as fractions of the box width / height (≤ 0). */
  left: number;
  top: number;
  /** The point inside the box, as fractions of the box width / height (0–1). */
  pinLeft: number;
  pinTop: number;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** A box on the page, as 0-1 fractions (a markup's strokes: `markupBBox`) */
export interface CropBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * @param point x / y are 0-1 page fractions (top-left origin); out-of-range values are clamped. Null for a markup-only
 *   location (no pin): the crop fits the strokes alone, or shows the whole page without them (no pin is drawn).
 * @param pageAspect page width / height
 * @param boxAspect box width / height
 * @param strokes Audit 3: the record's strokes on the page. When they reach past today's crop, the crop zooms out
 *   (never closer than `zoom`, never wider than the whole page) to show the pin and every stroke with a margin; with
 *   none, or all of them already in view, it is exactly today's crop around the pin.
 */
export function sheetPreviewCrop(point: { x: number; y: number } | null, pageAspect: number, boxAspect: number,
  zoom: number = SHEET_PREVIEW_ZOOM, strokes: CropBox | null = null): SheetPreviewCrop {
  const x = clamp(Number(point?.x) || 0, 0, 1);
  const y = clamp(Number(point?.y) || 0, 0, 1);
  // Page layer height in box heights, at a zoom.
  const heightAt = (z: number) => pageAspect > 0 && boxAspect > 0 ? (z * boxAspect) / pageAspect : z;
  // Centre (cx, cy), then clamp the layer so it still covers the box (a layer shorter than the box centres).
  const offset = (p: number, size: number) => size <= 1 ? (1 - size) / 2 : clamp(0.5 - p * size, 1 - size, 0);
  const at = (z: number, cx: number, cy: number): SheetPreviewCrop => {
    const width = z;
    const height = heightAt(z);
    const left = offset(cx, width);
    const top = offset(cy, height);
    return { width, left, top, pinLeft: left + x * width, pinTop: top + y * height };
  };
  const whole = pageAspect > 0 && boxAspect > 0 ? Math.min(1, pageAspect / boxAspect) : 1;
  if (!point && !strokes) {
    return at(whole, 0.5, 0.5);
  }
  const today = at(zoom, x, y);
  if (point && !strokes) {
    return today;
  }
  // The pin (when there is one) and the strokes, with a margin (a tenth of their extent, at least 2% of the page), on the page
  const ux0 = point ? Math.min(x, strokes.x0) : strokes.x0;
  const uy0 = point ? Math.min(y, strokes.y0) : strokes.y0;
  const ux1 = point ? Math.max(x, strokes.x1) : strokes.x1;
  const uy1 = point ? Math.max(y, strokes.y1) : strokes.y1;
  const mx = Math.max(0.02, (ux1 - ux0) * 0.1);
  const my = Math.max(0.02, (uy1 - uy0) * 0.1);
  const box = { x0: clamp(ux0 - mx, 0, 1), y0: clamp(uy0 - my, 0, 1), x1: clamp(ux1 + mx, 0, 1), y1: clamp(uy1 + my, 0, 1) };
  const inView = (crop: SheetPreviewCrop) => {
    const h = heightAt(crop.width);
    const eps = 1e-9;
    return crop.left + box.x0 * crop.width >= -eps && crop.left + box.x1 * crop.width <= 1 + eps
      && crop.top + box.y0 * h >= -eps && crop.top + box.y1 * h <= 1 + eps;
  };
  if (point && inView(today)) {
    return today;
  }
  // The closest zoom that shows the whole union (no closer than today's, no wider than the whole page), centred on it
  const w = box.x1 - box.x0;
  const h = box.y1 - box.y0;
  const fit = Math.min(w > 0 ? 1 / w : Infinity, h > 0 ? pageAspect / (boxAspect * h) : Infinity);
  return at(clamp(fit, whole, zoom), (box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2);
}
